import type { ServerToWeb, WebToServer } from "@walkie/protocol"
import { applyFrame, loadSnapshot } from "../store/actions"
import { getState, setState, type SocketStatus } from "../store/store"

/** The minimal WebSocket surface we use, so fixtures can stand in for a server. */
export interface SocketLike {
	readyState: number
	send(data: string): void
	close(code?: number, reason?: string): void
	onopen: ((ev: Event) => void) | null
	onmessage: ((ev: MessageEvent) => void) | null
	onclose: ((ev: CloseEvent) => void) | null
	onerror: ((ev: Event) => void) | null
}

/**
 * The `project` query value only routes the socket (one per project, so it can
 * land on that project's Durable Object); authentication is the first frame.
 */
let factory: (projectId: string) => SocketLike = projectId => {
	const proto = location.protocol === "https:" ? "wss:" : "ws:"
	return new WebSocket(
		`${proto}//${location.host}/ws/web?project=${encodeURIComponent(projectId)}`,
	) as SocketLike
}
export function setSocketFactory(f: (projectId: string) => SocketLike) {
	factory = f
}

const setStatus = (projectId: string, status: SocketStatus) =>
	setState(s =>
		s.sockets[projectId] === status
			? {}
			: { sockets: { ...s.sockets, [projectId]: status } },
	)

/**
 * One socket for one project. The token never goes in the URL. On every
 * reconnect, if this is the project on screen, its snapshot is refetched so
 * nothing missed while offline is lost.
 */
export function startProject(
	getToken: () => Promise<string>,
	projectId: string,
): () => void {
	let ws: SocketLike | null = null
	let stopped = false
	let attempt = 0
	let everReady = false
	let retry: ReturnType<typeof setTimeout> | null = null

	const schedule = () => {
		if (stopped) return
		const base = Math.min(15_000, 500 * 2 ** attempt)
		attempt++
		setStatus(projectId, attempt > 6 ? "offline" : "reconnecting")
		retry = setTimeout(open, base / 2 + Math.random() * base)
	}

	const open = async () => {
		retry = null
		if (stopped) return
		let token: string
		try {
			token = await getToken()
		} catch {
			setStatus(projectId, "offline")
			return
		}
		if (stopped) return
		const sock = factory(projectId)
		ws = sock
		sock.onopen = () => {
			const hello: WebToServer = { t: "auth", token, projectIds: [projectId] }
			sock.send(JSON.stringify(hello))
		}
		sock.onmessage = ev => {
			let f:
				(ServerToWeb & { projectId?: string }) | { t: "error" | "pong"; message?: string }
			try {
				f = JSON.parse(String(ev.data))
			} catch {
				return
			}
			if (f.t === "ready") {
				attempt = 0
				setStatus(projectId, "live")
				const s = getState()
				if (s.projectId === projectId && (everReady || !s.loading))
					void loadSnapshot(projectId)
				everReady = true
				return
			}
			if (f.t === "error" || f.t === "pong") return
			const frame = f as ServerToWeb & { projectId?: string }
			applyFrame({ ...frame, projectId: frame.projectId ?? projectId })
		}
		sock.onclose = () => {
			if (ws === sock) ws = null
			schedule()
		}
		sock.onerror = () => {}
	}

	const onOnline = () => {
		if (!ws && retry) {
			clearTimeout(retry)
			attempt = 0
			void open()
		}
	}
	window.addEventListener("online", onOnline)
	const ping = setInterval(() => {
		if (ws && ws.readyState === 1)
			ws.send(JSON.stringify({ t: "ping" } satisfies WebToServer))
	}, 25_000)

	setStatus(projectId, "connecting")
	void open()

	return () => {
		stopped = true
		if (retry) clearTimeout(retry)
		clearInterval(ping)
		window.removeEventListener("online", onOnline)
		const s = ws
		ws = null
		s?.close(1000, "bye")
	}
}

/** One socket per project the person can see; returns a function that closes them all. */
export function startRealtime(
	getToken: () => Promise<string>,
	projectIds: string[],
): () => void {
	const stops = projectIds.map(id => startProject(getToken, id))
	return () => {
		for (const stop of stops) stop()
		setState({ sockets: {} })
	}
}
